


/*
 * ----------------------------------------------------------------------------------
 * Imports - External
 * ----------------------------------------------------------------------------------
 */

import {
    CollaboratingGroupContext,
    CrfForm,
    GroupedFields,
    FormBreadcrumbs,
    IFileUpload,
    InstitutionContext,
    DatePicker,
    MasterGroupContext,
    PageLayout,
    PatientContext,
    RouteLoading,
    GroupedField,
    FileUpload,
    FormOptionsContext
} from '@ngt/opms';
import { Permission, usePermissionsByIds } from '@ngt/opms-bctapi';
import React, { FC, useContext } from 'react';


/*
 * ----------------------------------------------------------------------------------
 * Imports - Internal
 * ----------------------------------------------------------------------------------
 */
import * as Dtos from '../../../api/dtos';
import ImageReportDocuments from '../../documents/ImageReportDocuments';
import { Typography } from '@mui/material';
import { useFormActions, ValidateOn } from '@ngt/forms-core';
import { AppSettings } from '../../../AppSettings';


/*
 * ----------------------------------------------------------------------------------
 * Interface
 * ----------------------------------------------------------------------------------
 */

interface IImageReportUploadFormProps { }

/*
 * ---------------------------------------------------------------------------------
 * Styles
 * ---------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Functions
 * ---------------------------------------------------------------------------------
 */
// const hasSignedMainTrialConsentForm = ({ formState }: ICrfConditionParameters<Dtos.GeneralAndConsent>) => formState?.values.hasSignedMainTrialConsentForm === Dtos.LookupYesNoType.Yes;

const existingLink = (value?: IFileUpload) => {
    const file = value as Dtos.File;

    return `/opms/file/view/${file?.id}`;
}

const existingDownloadLink = (value?: IFileUpload) => {
    const file = value as Dtos.File;

    return `/opms/file/download/${file?.id}`;
}

/*
 * ----------------------------------------------------------------------------------
 * Components
 * ----------------------------------------------------------------------------------
 */
const InternalContents: FC = () => {
    const { data: masterGroup } = useContext(MasterGroupContext);
    const { data: collaboratingGroup } = useContext(CollaboratingGroupContext);
    const { data: institution } = useContext(InstitutionContext);
    const { data: patient } = useContext(PatientContext);

    const { setFieldValue, submit } = useFormActions();

    const { readOnly } = React.useContext(FormOptionsContext);

    const onFileUploadChange = React.useCallback((value?: IFileUpload) => {
        if(readOnly && value?.upload != null) {
            setFieldValue('submitType', 'save');

            submit();
        }
    }, [setFieldValue, submit, readOnly]);
    const {
        permissions: [
            canAdministerOpms
        ],
    } = usePermissionsByIds(
        [
            Permission.OpmsAdminister
        ], 
        masterGroup?.id, 
        collaboratingGroup?.id, 
        institution?.id, 
        patient?.id
    );

    return (
        <>
            <Typography sx={{px: 2, py: 3}}>
                Please upload all imaging reports for this participant. Once uploaded, 
                and participant is registered, these reports will be reviewed by the site 
                radiologist and data entry completed
            </Typography>
            <GroupedFields>
                <GroupedField
                    disableFuture
                    propertyPath="dateMriPerformed"
                    component={DatePicker}
                />
                <GroupedField
                    propertyPath="mriReportUpload"
                    component={FileUpload}
                    existingLink={existingLink}
                    existingDownloadLink={existingDownloadLink}
                    FieldGridProps={{
                        size: {
                            xl: 4,
                            lg: 4,
                            md: 4
                        }
                    }}
                    allowRedactOnFormDisable={canAdministerOpms ?? false} 
                    onChange={onFileUploadChange}
                />
            </GroupedFields>
            <ImageReportDocuments />
        </>
    );
};

const ImageReportUploadForm: React.FunctionComponent<IImageReportUploadFormProps> = () => {
    const { data: masterGroup } = useContext(MasterGroupContext);
    const { data: collaboratingGroup } = useContext(CollaboratingGroupContext);
    const { data: institution } = useContext(InstitutionContext);
    const { data: patient } = useContext(PatientContext);
    const {
        permissions: [
            canUpdateCrf,
        ],
        loading: permissionsLoading
    } = usePermissionsByIds(
        [
            Permission.OpmsCrfUpdate,
        ], 
        masterGroup?.id, 
        collaboratingGroup?.id, 
        institution?.id, 
        patient?.id
    );

    if (permissionsLoading) {
        return (
            <RouteLoading />
        );
    }

    return (
        <PageLayout
            breadcrumbs={<FormBreadcrumbs />}
        >
            <CrfForm
                validateOn={ValidateOn.onChange}
                readOnly={!canUpdateCrf}
                debug={AppSettings.debug}
            >
                <InternalContents />
            </CrfForm>
        </PageLayout>
    );
}


/*
 * ----------------------------------------------------------------------------------
 * Default Export
 * ----------------------------------------------------------------------------------
 */

export default ImageReportUploadForm;
